import { Box, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';

// Components
import colorSystem from 'src/scss/_colors-system.module.scss';
import { BookmarkTypes, IBookmark } from 'src/model/bookmark';
import IconFeather from 'src/components/common/Icon';
import { Edit, Trash } from 'react-feather';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DropdownMenu from 'src/components/DropdownMenu';
import { IMenuItem } from 'src/components/Drawer/SidebarMenu';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import { useMutateDeleteBookmark, useMutatePutBookmark } from 'src/services/mutate/useMutateBookmark';

interface IBookmarkCardLayoutProps {
  children: JSX.Element;
  data?: IBookmark;
  bookmarkType: BookmarkTypes;
}

const StyledBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  cursor: 'pointer',
  position: 'relative',
}));

const BookmarkCardLayout = (props: IBookmarkCardLayoutProps) => {
  const { children, data, bookmarkType } = props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { mutateAsync: updateBookmarkMutateAsync } = useMutatePutBookmark();
  const { mutateAsync: deleteBookmarkMutateAsync } = useMutateDeleteBookmark();
  const [note, setNote] = useState<string>(data?.note || '');

  const [isOpenBookmarkNoteForm, setIsOpenBookmarkNoteForm] = useState(false);
  const [isMutatingBookmark, setIsMutatingBookmark] = useState(false);

  const handleCloseBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(false);
  };

  const handleOpenBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(true);
  };

  const handleSubmitBookmarkNoteForm = useCallback(async () => {
    setIsMutatingBookmark(true);
    await updateBookmarkMutateAsync({
      type: bookmarkType,
      bookmarkId: data?.uuid || '',
      body: {
        note,
      },
    });

    setIsMutatingBookmark(false);

    handleCloseBookmarkNoteForm();
  }, [data?.uuid, note]);

  const deleteBookmarkFn = useCallback(async () => {
    await deleteBookmarkMutateAsync({
      type: bookmarkType,
      bookmarkId: data?.uuid || '',
    });
  }, [data?.uuid]);

  const menuData: IMenuItem[] = [
    {
      text: 'Edit note',
      icon: <IconFeather muiProps={{ sx: { width: '18px', fontSize: 24 } }} icon={<Edit />} />,
      key: 'edit_note',
      onClickFn: () => {
        handleOpenBookmarkNoteForm();
        handleMenuClose();
      },
      path: AuthRouteKeys.Setting,
      disabled: false,
    },
    {
      text: 'Remove',
      onClickFn: () => {
        deleteBookmarkFn();
        handleMenuClose();
      },
      icon: <IconFeather muiProps={{ sx: { width: '18px', fontSize: 24 } }} icon={<Trash />} />,
      key: 'delete_note',
      path: AuthRouteKeys.TradeData,
      disabled: false,
    },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  return (
    <StyledBox
      sx={{
        borderColor: theme.palette.divider,
      }}
      display={'flex'}
      flexDirection="column"
      height="100%"
    >
      <IconButton
        aria-label="more"
        id="long-button"
        // aria-controls={open ? 'long-menu' : undefined}
        // aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleOpenUserMenu}
        sx={{
          position: 'absolute',
          right: theme.spacing(1),
          top: theme.spacing(1),
        }}
      >
        <MoreVertIcon fontSize={'small'} />
      </IconButton>
      <DropdownMenu menuData={menuData} anchorEl={anchorEl} handleMenuClose={handleMenuClose} />
      <Stack height="100%" direction={'column'} gap={0.5} justifyContent="space-between">
        <Box padding={2} paddingTop={1.5}>
          {children}
        </Box>
        {data?.note && (
          <Box
            sx={{
              backgroundColor: colorSystem.backgroundPaper1,
              borderBottomRightRadius: theme.shape.borderRadius * 2,
              borderBottomLeftRadius: theme.shape.borderRadius * 2,
              paddingX: 2,
              paddingY: 1.5,
            }}
          >
            <Typography color={'primary'} variant={'caption'}>
              Note:{' '}
            </Typography>
            <Typography color={'text.secondary'} variant={'caption'}>
              {data?.note}
            </Typography>
          </Box>
        )}
      </Stack>
      <FormBookmarkNote
        open={isOpenBookmarkNoteForm}
        handleClose={handleCloseBookmarkNoteForm}
        handleSubmit={() => handleSubmitBookmarkNoteForm()}
        value={note}
        onChange={(value) => setNote(value)}
        isLoading={isMutatingBookmark}
      />
    </StyledBox>
  );
};

export default BookmarkCardLayout;
